// src/layouts/measure-text.ts
var wordCache = new Map;
var takeMeasurement = ({
  text,
  fontFamily,
  fontSize,
  fontWeight,
  letterSpacing,
  fontVariantNumeric,
  additionalStyles,
  textTransform
}) => {
  if (typeof document === "undefined") {
    throw new Error("measureText() can only be called in a browser.");
  }
  const node = document.createElement("span");
  if (fontFamily) {
    node.style.fontFamily = fontFamily;
  }
  node.style.display = "inline-block";
  node.style.position = "absolute";
  node.style.top = `-10000px`;
  node.style.whiteSpace = "pre";
  node.style.fontSize = typeof fontSize === "string" ? fontSize : `${fontSize}px`;
  if (additionalStyles) {
    for (const key of Object.keys(additionalStyles)) {
      node.style[key] = additionalStyles[key];
    }
  }
  if (fontWeight) {
    node.style.fontWeight = fontWeight.toString();
  }
  if (letterSpacing) {
    node.style.letterSpacing = letterSpacing;
  }
  if (fontVariantNumeric) {
    node.style.fontVariantNumeric = fontVariantNumeric;
  }
  if (textTransform) {
    node.style.textTransform = textTransform;
  }
  node.innerText = text;
  document.body.appendChild(node);
  const computedFontFamily = window.getComputedStyle(node).fontFamily;
  const boundingBox = node.getBoundingClientRect();
  document.body.removeChild(node);
  return {
    boundingBox,
    computedFontFamily
  };
};
var measureText = ({
  text,
  fontFamily,
  fontSize,
  fontWeight,
  letterSpacing,
  fontVariantNumeric,
  validateFontIsLoaded,
  additionalStyles,
  textTransform
}) => {
  const key = `${text}-${fontFamily}-${fontWeight}-${fontSize}-${letterSpacing}-${textTransform}-${JSON.stringify(additionalStyles)}`;
  if (wordCache.has(key)) {
    return wordCache.get(key);
  }
  const { boundingBox, computedFontFamily } = takeMeasurement({
    fontFamily,
    fontSize,
    text,
    fontVariantNumeric,
    fontWeight,
    letterSpacing,
    additionalStyles,
    textTransform
  });
  if (validateFontIsLoaded && text.trim().length > 0) {
    const {
      boundingBox: boundingBoxOfFallbackFont,
      computedFontFamily: computedFallback
    } = takeMeasurement({
      fontFamily: null,
      fontSize,
      text,
      fontVariantNumeric,
      fontWeight,
      letterSpacing,
      additionalStyles,
      textTransform
    });
    const sameAsFallbackFont = boundingBox.height === boundingBoxOfFallbackFont.height && boundingBox.width === boundingBoxOfFallbackFont.width;
    if (sameAsFallbackFont && computedFallback !== computedFontFamily && new Set(text).size > 4) {
      const err = [
        `Called measureText() with "fontFamily": ${JSON.stringify(fontFamily)} but it looks like the font is not loaded at the time of calling.`,
        `A measurement with the fallback font ${computedFallback} was taken and had the same dimensions, indicating that the browser used the fallback font.`,
        "See https://remotion.dev/docs/layout-utils/best-practices for best practices."
      ];
      throw new Error(err.join(`
`));
    }
  }
  const result = { height: boundingBox.height, width: boundingBox.width };
  wordCache.set(key, result);
  return result;
};

// src/layouts/fill-text-box.ts
var fillTextBox = ({
  maxBoxWidth,
  maxLines
}) => {
  const lines = new Array(maxLines).fill(0).map(() => []);
  return {
    add: ({
      text,
      fontFamily,
      fontWeight,
      fontSize,
      letterSpacing,
      fontVariantNumeric,
      validateFontIsLoaded,
      textTransform,
      additionalStyles
    }) => {
      const lastLineIndex = lines.reduceRight((acc, curr, index) => {
        if (acc === -1 && curr.length > 0) {
          return index;
        }
        return acc;
      }, -1);
      const currentlyAt = lastLineIndex === -1 ? 0 : lastLineIndex;
      const lineToUse = lines[currentlyAt];
      const lineWithWord = [
        ...lineToUse,
        {
          text,
          fontFamily,
          fontWeight,
          fontSize,
          letterSpacing,
          fontVariantNumeric,
          validateFontIsLoaded,
          textTransform,
          additionalStyles
        }
      ];
      const widths = lineWithWord.map((w) => measureText(w).width);
      const lineWidthWithWordAdded = widths.reduce((a, b) => a + b, 0);
      if (Math.ceil(lineWidthWithWordAdded) <= maxBoxWidth) {
        lines[currentlyAt].push({
          text: lines[currentlyAt].length === 0 ? text.trimStart() : text,
          fontFamily,
          fontWeight,
          fontSize,
          letterSpacing,
          textTransform,
          fontVariantNumeric,
          validateFontIsLoaded,
          additionalStyles
        });
        return { exceedsBox: false, newLine: false };
      }
      if (currentlyAt === maxLines - 1) {
        return { exceedsBox: true, newLine: false };
      }
      const wordForNewLine = {
        text: text.trimStart(),
        fontFamily,
        fontWeight,
        fontSize,
        letterSpacing,
        fontVariantNumeric,
        validateFontIsLoaded,
        textTransform,
        additionalStyles
      };
      const wordAloneWidth = measureText(wordForNewLine).width;
      if (Math.ceil(wordAloneWidth) > maxBoxWidth) {
        return { exceedsBox: true, newLine: false };
      }
      lines[currentlyAt + 1] = [wordForNewLine];
      return { exceedsBox: false, newLine: true };
    }
  };
};
// src/layouts/fit-text.ts
var sampleSize = 100;
var fitText = ({
  text,
  withinWidth,
  fontFamily,
  fontVariantNumeric,
  fontWeight,
  letterSpacing,
  validateFontIsLoaded,
  additionalStyles,
  textTransform
}) => {
  const estimate = measureText({
    text,
    fontFamily,
    fontSize: sampleSize,
    fontWeight,
    fontVariantNumeric,
    letterSpacing,
    validateFontIsLoaded,
    textTransform,
    additionalStyles
  });
  return { fontSize: withinWidth / estimate.width * sampleSize };
};
// src/layouts/fit-text-on-n-lines.ts
var PRECISION = 100;
var fitTextOnNLines = ({
  text,
  maxLines,
  maxBoxWidth,
  fontFamily,
  fontWeight,
  letterSpacing,
  fontVariantNumeric,
  validateFontIsLoaded,
  textTransform,
  additionalStyles,
  maxFontSize
}) => {
  const minFontSize = 0.1;
  let left = Math.floor(minFontSize * PRECISION);
  let right = Math.floor((maxFontSize ?? 2000) * PRECISION);
  let optimalFontSize = minFontSize;
  let optimalLines = [];
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const fontSize = mid / PRECISION;
    const textBox = fillTextBox({
      maxBoxWidth,
      maxLines
    });
    const words = text.split(" ");
    let exceedsBox = false;
    let currentLine = 0;
    const lines = [""];
    for (const word of words) {
      const result = textBox.add({
        text: lines[currentLine].length === 0 ? word : " " + word,
        fontFamily,
        fontWeight,
        fontSize,
        letterSpacing,
        fontVariantNumeric,
        validateFontIsLoaded,
        textTransform,
        additionalStyles
      });
      if (result.exceedsBox) {
        exceedsBox = true;
        break;
      }
      if (result.newLine) {
        lines.push("");
        currentLine++;
      }
      lines[currentLine] += word + " ";
    }
    if (!exceedsBox && currentLine < maxLines) {
      optimalFontSize = fontSize;
      optimalLines = lines;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  for (let i = 0;i < optimalLines.length; i++) {
    optimalLines[i] = optimalLines[i].trimEnd();
  }
  return {
    fontSize: optimalFontSize,
    lines: optimalLines
  };
};
export {
  fillTextBox,
  fitText,
  fitTextOnNLines,
  measureText
};
